<script setup lang="ts">
    import { computed, reactive, watch, onMounted, inject, h, type ComputedRef } from 'vue';
    import Loading from '@shared/components/Loading.vue';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import { getResponse, downloadAllSurveyResources } from '../SurveyStatistics.access';
    import { useSurveyResourcePreviews } from '../use-survey-resource-previews';
    import { QuestionType, type Question, type SurveyResponseDetail } from '../../SurveysTypes.d';
    import QuestionMetaBadges from './QuestionMetaBadges.vue';
    import QuestionAnswer from './QuestionAnswer.vue';
    import { layoutResponseQuestions } from '../SurveyStatistics.utils';

    const props = defineProps<{
        surveyId: string;
        responseId: string;
    }>();

    const questions = inject<ComputedRef<Question[]>>(
        'surveyStatisticsQuestions',
        computed(() => [])
    );

    const surveyTitle = inject<ComputedRef<string>>(
        'surveyStatisticsTitle',
        computed(() => '')
    );

    const state = reactive<{
        response: SurveyResponseDetail | null;
        loading: boolean;
    }>({
        response: null,
        loading: false,
    });

    const { resourcePreviewUrls, clearResourcePreviewUrls, loadResourcePreviews } = useSurveyResourcePreviews();

    async function load() {
        state.loading = true;
        clearResourcePreviewUrls();
        try {
            state.response = await getResponse(props.surveyId, props.responseId);
            loadResourcePreviews(state.response.SurveyAnswers.flatMap((answer) => answer.SurveyAnswerResources));
        } finally {
            state.loading = false;
        }
    }

    watch(() => props.responseId, load);
    onMounted(load);

    const atleastOneResource = computed(() => !!state.response?.SurveyAnswers.some((a) => a.SurveyAnswerResources.length > 0));

    const GUID_EMPTY = '00000000-0000-0000-0000-000000000000';

    function contactName(): string {
        const contact = state.response?.Contact;
        if (!contact) return '';
        return [contact.FirstName, contact.LastName].filter(Boolean).join(' ') || contact.Email || $Resources.YourContactLabel;
    }

    function ResponseTitle() {
        return [
            `${$Resources.HereIsContactsResponseFirstPartLabel} `,
            h('i', contactName()),
            `${$Resources.HereIsContactsResponseSecondPartLabel} `,
            h('i', surveyTitle.value),
        ];
    }

    function contactRoute() {
        return {
            name: 'SurveyStatisticsResponseContact',
            params: { surveyId: props.surveyId, contactId: state.response?.Contact.ContactId },
        };
    }

    const nonContentQuestionTypes = [QuestionType.Content, QuestionType.Pagebreak, QuestionType.Divider];

    const answeredQuestions = computed(() => {
        if (!state.response) return [];

        const contentQuestions = questions.value.filter((question) => !nonContentQuestionTypes.includes(question.Type));

        return layoutResponseQuestions(contentQuestions).map((entry) => {
            const answer = state.response!.SurveyAnswers.find((a) => a.Question.QuestionId === entry.question.QuestionId);
            const resources = answer?.SurveyAnswerResources ?? [];

            return {
                ...entry,
                answered: entry.question.Type === QuestionType.FileUpload ? resources.length > 0 : !!answer,
                freeText: answer?.FreeText ?? null,
                answerOptions: answer?.Question.QuestionOptions ?? [],
                resources,
            };
        });
    });
</script>

<template>
    <template v-if="state.response">
        <header class="list-header">
            <h2>
                <component :is="ResponseTitle" />
            </h2>

            <ul class="list-unstyled d-flex gap-lg flex-wrap">
                <li v-if="state.response.Contact.ContactId === GUID_EMPTY">
                    <small>{{ $Resources.ResponseRespondent }}:</small>
                    <span class="fw-600 d-block">{{ $Resources.AnonymousNameLabel }}</span>
                </li>
                <li v-else>
                    <small>{{ $Resources.ResponseRespondent }}:</small>
                    <router-link class="fw-600 d-block" :to="contactRoute()">{{ contactName() }}</router-link>
                </li>

                <li v-if="state.response.CultureEnglishName">
                    <small>{{ $Resources.SurveyStatisticsCulturesChartHeading }}:</small>
                    <span class="fw-600 d-block">{{ state.response.CultureEnglishName }}</span>
                </li>
                <li>
                    <small>{{ $Resources.ResponseCompleted }}:</small>
                    <span class="fw-600 d-block">{{ formatDateTime(state.response.Created) }}</span>
                </li>
            </ul>

            <div class="form-group m-sm-b" v-if="atleastOneResource">
                <button type="button" class="btn btn-default btn-sm" @click="downloadAllSurveyResources(props.responseId)">
                    {{ $Resources.DownloadAllResponseFiles }}
                </button>
            </div>
        </header>

        <div class="container-fluid m-lg-t m-xs-b">
            <template v-for="entry in answeredQuestions" :key="entry.question.QuestionId">
                <h3 v-if="entry.isMatrixHeader" class="m-0-t matrix-group-header">
                    {{ entry.label }} {{ entry.question.Title }}
                    <small class="fw-600">{{ $Resources.QuestionTypeMatrixLabel }}</small>
                    <small v-if="entry.question.Required">* {{ $Resources.QuestionRequiredLabel }}</small>
                </h3>

                <article
                    v-else
                    class="question-card"
                    :class="{
                        nested: entry.question.ParentQuestionId,
                        'matrix-child': entry.isMatrixChild,
                        'matrix-child-first': entry.isFirstMatrixChild,
                        'matrix-child-last': entry.isLastMatrixChild,
                    }">
                    <header>
                        <QuestionMetaBadges :question="entry.question" :answered="entry.answered" :show-average="true" />

                        <h3 class="m-0-t">{{ entry.label }} {{ entry.question.Title }}</h3>
                    </header>

                    <div v-if="entry.question.Text" class="m-sm-b" v-html="entry.question.Text"></div>

                    <QuestionAnswer
                        :question="entry.question"
                        :free-text="entry.freeText"
                        :answer-options="entry.answerOptions"
                        :resources="entry.resources"
                        :response-id="props.responseId"
                        :resource-preview-urls="resourcePreviewUrls" />
                </article>
            </template>
        </div>
    </template>

    <Loading :show="state.loading" loadingStyle="progress-bar" fixed></Loading>
</template>

<style lang="less" src="../question-card.less"></style>
